import axios, { AxiosInstance } from 'axios';

export interface ParkingLocation {
  _id: string;
  areaDescription: string;
  parkingLot: string;
  parentLocation?: string;
  totalSpaces: number;
  actualCapacity: number;
  isChargeable: boolean;
  notes?: string;
  floorPlan?: {
    filename: string;
    originalName: string;
    uploadDate: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface LocationFormData {
  areaDescription: string;
  parkingLot: string;
  parentLocation?: string;
  totalSpaces: number;
  actualCapacity: number;
  isChargeable: boolean;
  notes?: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface LocationsResponse {
  locations: ParkingLocation[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
    pages: number;
  };
}

class LocationService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 30000, // 增加超時時間以支持文件上傳
    });

    // 請求攔截器 - 添加認證token（容錯讀取 token/authToken，localStorage 與 sessionStorage）
    this.api.interceptors.request.use(
      (config) => {
        const token =
          localStorage.getItem('token') ||
          localStorage.getItem('authToken') ||
          sessionStorage.getItem('token') ||
          sessionStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 響應攔截器
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // 獲取所有地點
  async getLocations(): Promise<ApiResponse<LocationsResponse>> {
    try {
      const response = await this.api.get('/locations');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '獲取地點列表失敗');
    }
  }

  // 獲取地點樹狀結構
  async getLocationTree(): Promise<ApiResponse<ParkingLocation[]>> {
    try {
      const response = await this.api.get('/locations/tree');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '獲取地點樹狀結構失敗');
    }
  }

  // 獲取單個地點
  async getLocation(id: string): Promise<ApiResponse<ParkingLocation>> {
    try {
      const response = await this.api.get(`/locations/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '獲取地點詳情失敗');
    }
  }

  // 創建地點
  async createLocation(locationData: LocationFormData, floorPlan?: File): Promise<ApiResponse<ParkingLocation>> {
    try {
      const formData = new FormData();
      
      // 添加表單數據
      Object.entries(locationData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      
      // 添加平面圖文件
      if (floorPlan) {
        formData.append('floorPlan', floorPlan);
      }
      
      const response = await this.api.post('/locations', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '創建地點失敗');
    }
  }

  // 更新地點
  async updateLocation(id: string, locationData: LocationFormData, floorPlan?: File): Promise<ApiResponse<ParkingLocation>> {
    try {
      const formData = new FormData();
      
      // 添加表單數據
      Object.entries(locationData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      
      // 添加平面圖文件
      if (floorPlan) {
        formData.append('floorPlan', floorPlan);
      }
      
      const response = await this.api.put(`/locations/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '更新地點失敗');
    }
  }

  // 刪除地點
  async deleteLocation(id: string): Promise<ApiResponse> {
    try {
      const response = await this.api.delete(`/locations/${id}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '刪除地點失敗');
    }
  }

  // 獲取平面圖
  async getFloorPlan(id: string): Promise<Blob> {
    try {
      const response = await this.api.get(`/locations/${id}/floorplan`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '獲取平面圖失敗');
    }
  }

  // 刪除平面圖
  async deleteFloorPlan(id: string): Promise<ApiResponse> {
    try {
      const response = await this.api.delete(`/locations/${id}/floorplan`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '刪除平面圖失敗');
    }
  }

  // 依地點生成車位
  async generateSpots(
    id: string,
    payload: {
      floor?: string;
      zone?: string;
      counts: {
        standard?: number;
        compact?: number;
        large?: number;
        handicapped?: number;
        electric?: number;
        vip?: number;
        visitor?: number;
      };
    }
  ): Promise<ApiResponse<{ createdCount: number; floor: string; zone: string }>> {
    try {
      const response = await this.api.post(`/locations/${id}/generate-spots`, payload);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || '生成車位失敗');
    }
  }
}

export const locationService = new LocationService();
export default locationService;